import type { editProxyFpss } from './editProxyFps';
import { StreamRoleId, type MediaAvailability } from './mediaAvailability';
import type { MediaThroughput } from './progress';

export type MediaSegmentsResponse = Record<string, Record<number, string>>;

export type StreamSetPreference = StreamRoleId[][];

export const SEGMENTED_HIGHRES_TS_STREAM_ID = StreamRoleId.LiveRecordHighresTs;

export function stringToStreamRoleId(roleString: string): StreamRoleId | undefined {
  const allowedStreamIdsArray = Object.values(StreamRoleId);
  for (const allowedStreamId of allowedStreamIdsArray) {
    if (roleString == allowedStreamId) {
      return allowedStreamId;
    }
  }
  return undefined;
}

export function isVideoStreamRole(role: StreamRoleId): boolean {
  switch (role) {
    case StreamRoleId.BrowsePreviewVideo:
    case StreamRoleId.BrowsePreviewVideoAudioDownMix:
    case StreamRoleId.EditProxyFull:
    case StreamRoleId.LiveRecordHighresTs:
    case StreamRoleId.LiveRecordWebproxyTs:
      return true;

    default:
      return false;
  }
}

/**
 * State of an incremental ingest.
 *
 * When Mimir is informed that there is a file of a given size available for
 * incremental ingest, here is what it should consider:
 *
 *  * No item exists for this file.
 *    * Create item
 *  * Item exists, no ingest task is running, and file size does not match
 *    completed bytes.
 *    * Start new ongoing ingest task
 *  * Item exists and ingest task exists, but timestamp is not recent.
 *    * Kill and delete existing ongoing task.
 *  * Item exists, ingest task exists, and timestamp is recent.
 *    * No action needed.
 *  * Item exists, no ingest task is running, and file size matches completed
 *    bytes.
 *    * No action needed. (TODO: Consider moving file after completion.)
 */
export interface ItemMediaBase {
  /**
   * The item this ingest pertains to.
   */
   id: string,

   /**
    * Note: Unlike in ExtendedItemDto, media availability is not optional here.
    */
   media: MediaAvailability,
}

export interface ItemMediaDto extends ItemMediaBase {
  throughput?: MediaThroughput
}

export function isMediaAvailability(val: unknown): val is MediaAvailability {
  if (val == null) {
    return false;
  }

  const obj = val as Partial<MediaAvailability>;
  return (
    obj.segmentDuration != null &&
    obj.codecsByStreamId != null &&
    obj.segmentCount != null &&
    obj.streaming != null
  );
}

export type MediaAvailabilityStatusType = 'streaming';
export const mediaAvailabilityStatusList: MediaAvailabilityStatusType[] = ['streaming'];

export const MediaAvailabilityStatus: {
  STREAMING: MediaAvailabilityStatusType;
} = {
  STREAMING: 'streaming'
};

export type SegmentType = 'mp4' | 'ts';

// Unlike segmented proxies that can be either mp4 or ts, only ts
// is supported for segmented highres
export const SEGMENTED_HIGHRES_EXTENSION = '.ts';

export const typesOfSourceMedia = <const>['highRes', 'editProxy'];
export type TypesOfSourceMedia = typeof typesOfSourceMedia[number];

/**
 * Common parameters for transcoding media into different formats
 */
export interface BaseMediaConversionParameters {
  container: string;
}

export const SupportedVideoConversionCodecs = <const>[
  { label: 'AVC-Intra Class 100', value: 'avcintra-class=100' },
  { label: 'XDCAM 422', value: 'xdcamhd422' },
  { label: 'MPEG-2', value: 'mpeg2video' },
  { label: 'H264', value: 'h264' },

  // The following two entries are kept to be backward compatible as we once used
  // to differentiate between profiles, but have removed the distinction since.
  { label: 'H264', value: 'h264|high' },
  { label: 'H264', value: 'h264|main' },

  { label: 'Apple ProRes 422 HQ HDR', value: 'prores_hq_hdr' },
  { label: 'Apple ProRes 422 HQ SDR', value: 'prores_hq_sdr' },
  { label: 'XAVC Intra HDR', value: 'xavc_hdr' },
  { label: 'XAVC Intra SDR', value: 'xavc_sdr' },
];
export const SupportedVideoConversionResolutions = <const>[
  { label: '1280x720', value: '1280:720' },
  { label: '1920x1080', value: '1920:1080' },
  { label: '2560x1440 (2K)', value: '2560:1440' },
  { label: '3840x2160 (4K)', value: '3840:2160' },
];
export const SupportedVideoConversionContainerFormats = <const>[
  { label: 'MXF', value: 'mxf' },
  { label: 'MP4', value: 'mp4' },
  { label: 'MPEG-PS', value: 'mpeg' },
  { label: 'MOV', value: 'mov' },
];
export const SupportedVideoConversionFieldOrders = <const>[
  { label: 'Same as Source', value: 'ss'},
  { label: 'Progressive', value: 'progressive' },
  { label: 'Upper first', value: 'tt' },
  { label: 'Lower first', value: 'bb' },
];

/**
 * Parameters used to convert a video into a specific format using tool like ffmpeg.
 */
export interface VideoConversionParameters extends BaseMediaConversionParameters {
  codec: typeof SupportedVideoConversionCodecs[number]['value'];
  resolution: typeof SupportedVideoConversionResolutions[number]['value'];
  framerate: typeof editProxyFpss[number]['key'];
  container: typeof SupportedVideoConversionContainerFormats[number]['value'];
  fieldOrder?: typeof SupportedVideoConversionFieldOrders[number]['value'];
}

